import { type ChangeEvent, useCallback } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod/v4';
import { Grid } from '@interxfi/design-system/components/alignment/grid';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ResponsiveValue } from '@interxfi/design-system/util/responsive';
import type { IxcRegistrationV1AffiliationsCorporateInsider } from '@interxfi/types/generated/api';
import { AppCheckboxInputWithLens } from '../../../../../../components/form/app-checkbox-input';
import { AppTextInputWithLens } from '../../../../../../components/form/app-text-input';

export const corporateInsiderFormSchema = z.object({
  isProviding: z.boolean(),
  value: z
    .object({
      companyName: z.string().nonempty({ error: 'Company Name must be provided' }),
      symbol: z.string().nonempty({ error: 'Symbol must be provided' }),
    })
    .optional(),
});

export type CorporateInsiderFormValues = z.infer<typeof corporateInsiderFormSchema>;

export const defaultCorporateInsiderFormValues: CorporateInsiderFormValues = {
  isProviding: false,
};

export function formValuesToCorporateInsider(values: CorporateInsiderFormValues): IxcRegistrationV1AffiliationsCorporateInsider | undefined {
  if (!values.value) {
    return undefined;
  }

  return {
    companyName: values.value.companyName,
    symbol: values.value.symbol,
  };
}

export function getInitialFormValuesForCorporateInsider(
  corporateInsider: IxcRegistrationV1AffiliationsCorporateInsider | undefined,
): CorporateInsiderFormValues | undefined {
  if (!corporateInsider) {
    return defaultCorporateInsiderFormValues;
  }

  return {
    isProviding: true,
    value: {
      companyName: corporateInsider.companyName,
      symbol: corporateInsider.symbol,
    },
  };
}

const columns: ResponsiveValue<number> = {
  sm: 1,
  md: 2,
};

interface CorporateInsiderFormProps {
  lens: Lens<CorporateInsiderFormValues>;
}

export function CorporateInsiderForm({ lens }: CorporateInsiderFormProps) {
  const { setValue } = useFormContext();
  const isProvidingLens = lens.focus('isProviding');
  const isProviding = useWatch(isProvidingLens.interop());
  const { name: valueName } = lens.focus('value').interop();
  const handleAffiliatedCorporateInsiderToggle = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setValue(valueName, e.target.checked ? { companyName: '', symbol: '' } : undefined, { shouldValidate: true });
    },
    [valueName, setValue],
  );

  return (
    <VerticalStack gap={7}>
      <AppCheckboxInputWithLens
        label={
          <Typography lineHeight="1.125rem" size="0.875rem">
            This individual is a{' '}
            <Typography lineHeight="1.125rem" size="0.875rem" weight="bold">
              Corporate Insider
            </Typography>
            , meaning they are an officer, director, or 10%+ shareholder of a public company.
          </Typography>
        }
        lens={isProvidingLens}
        onChange={handleAffiliatedCorporateInsiderToggle}
      />

      {isProviding ? (
        <Grid autoColumns={columns} gap={4}>
          <AppTextInputWithLens label="Company Name" lens={lens.focus('value.companyName').defined()} required />
          <AppTextInputWithLens label="Symbol" lens={lens.focus('value.symbol').defined()} required />
        </Grid>
      ) : null}
    </VerticalStack>
  );
}
